import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { fetchPayAppDetail } from '../../api/projects'
import { formatUsd } from './pulseTotals.js'
import PayAppEditorModal from '../../screens/projects/modal/PayAppEditorModal'
import PulsePaymentsSection from './PulsePaymentsSection'
import { fetchPulseFileBlob } from './pulseApi'
import { pulsePayAppTitle } from './pulsePayAppTitle.js'
import { usePulseDrawerAction } from './PulseDrawer.tsx'
import type { PulsePaymentRow, PulsePaymentsPayload } from './pulseApi'

export default function PulsePayAppPanel({
  projectId,
  payAppId,
  payments,
  onClose,
  onOpenPayment,
}: {
  projectId: number
  payAppId: number
  payments?: PulsePaymentsPayload | null
  onClose: () => void
  onOpenPayment: (row: PulsePaymentRow) => void
}) {
  const q = useQuery({
    queryKey: ['pay-app-detail', payAppId],
    queryFn: () => fetchPayAppDetail(payAppId),
  })
  const [preview, setPreview] = useState('')
  const pa = q.data
  const pdfName = pa ? `PayApp_${pa.app_number}.pdf` : 'PayApp.pdf'

  usePulseDrawerAction(() => (
    <button
      type="button"
      className="btn b-dk btn-sm pulse-drawer-download"
      data-testid="pulse-pa-download-btn"
      onClick={async () => {
        const blob = await fetchPulseFileBlob(`/api/payapps/${payAppId}/pdf?inline=1`)
        const href = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = href
        a.download = pdfName
        a.click()
        URL.revokeObjectURL(href)
      }}
    >
      Download
    </button>
  ), [payAppId, pdfName], !!pa && pa.status !== 'draft')

  useEffect(() => {
    if (!pa || pa.status === 'draft') return
    let cancelled = false
    let objectUrl = ''
    fetchPulseFileBlob(`/api/payapps/${payAppId}/html`)
      .then(async (blob) => {
        if (cancelled) return
        const html = (await blob.text()).replace(/window\.print\s*\(\s*\)/g, '')
        objectUrl = URL.createObjectURL(new Blob([html], { type: 'text/html' }))
        setPreview(objectUrl)
      })
      .catch(() => {
        if (!cancelled) setPreview('')
      })
    return () => {
      cancelled = true
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [payAppId, pa])

  if (q.isLoading) return <p>Loading…</p>
  if (!pa) return <p>Pay application not found.</p>
  const proposedBy = (pa as { proposed_by_client?: string | null }).proposed_by_client
  const proposedBadge = proposedBy
    ? `Proposed by ${proposedBy.replace(/ #\d+$/, '').replace(/\s+Desktop$/i, '')}`
    : null
  if (pa.status === 'draft') {
    return (
      <div data-testid="pulse-pa-draft">
        {proposedBadge && (
          <div
            data-testid="proposed-by-client"
            className="mb-2 inline-flex rounded-full bg-[#fff7ed] px-3 py-1 text-xs font-semibold text-[#E8622A]"
          >
            {proposedBadge}
          </div>
        )}
        <PayAppEditorModal
          projectId={projectId}
          payAppId={payAppId}
          isOpen
          embedded
          onClose={onClose}
          onSaved={onClose}
        />
      </div>
    )
  }
  return (
    <div data-testid="pulse-pa-view">
      <h2 className="pulse-detail-title">{pulsePayAppTitle(pa.app_number, pa.period_label)}</h2>
      <div className="pulse-detail-metrics">
        <div>
          <label>Application amount</label>
          <strong>{formatUsd(pa.amount_due)}</strong>
        </div>
      </div>
      {preview ? (
        <div className="pulse-viewer">
          <iframe
            title={`Pay app ${pa.app_number} PDF`}
            src={preview}
            data-testid="pulse-pa-preview"
          />
        </div>
      ) : (
        <p data-testid="pulse-pa-preview-loading">Loading PDF…</p>
      )}
      <PulsePaymentsSection payload={payments} payAppId={payAppId} onOpen={onOpenPayment} />
    </div>
  )
}
